import React, { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { LinkBreak } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import { useCommandFeed } from "@/hooks/useCommandFeed";
import { CommandCentre } from "@/components/admin/command/CommandCentre";
import { ScaleCanvas } from "@/components/admin/command/ScaleCanvas";

const RELOAD_MS = 6 * 60 * 60 * 1000;

// Keeps the TV awake and reloads the page every few hours so a wall screen never runs stale code.
function useKioskScreen() {
  useEffect(() => {
    document.title = "OTM Command Centre";
    let lock = null;
    const wake = () => navigator.wakeLock?.request("screen").then((l) => { lock = l; }).catch(() => {});
    const onVis = () => document.visibilityState === "visible" && wake();
    wake();
    document.addEventListener("visibilitychange", onVis);
    const t = setTimeout(() => window.location.reload(), RELOAD_MS);
    return () => { document.removeEventListener("visibilitychange", onVis); clearTimeout(t); lock?.release?.().catch(() => {}); };
  }, []);
}

function Message({ invalid }) {
  return (
    <div className="fixed inset-0 bg-[#050505] flex flex-col items-center justify-center gap-5 text-center px-8" data-testid={invalid ? "display-invalid" : "display-loading"}>
      <img src={ASSETS.logo} alt="On The Move" className="h-12 w-auto object-contain" />
      {invalid ? (
        <>
          <LinkBreak size={40} weight="duotone" className="text-amber-400" />
          <h1 className="font-heading text-3xl font-bold text-white">This display link is no longer valid</h1>
          <p className="text-neutral-400 max-w-md">Ask an OTM admin for the new TV link from Admin → Command Centre → TV link.</p>
        </>
      ) : (
        <>
          <span className="w-10 h-10 rounded-full border-2 border-neutral-800 border-t-[#73bf44] animate-spin" />
          <p className="text-neutral-400">Loading the Command Centre…</p>
        </>
      )}
    </div>
  );
}

export default function CommandDisplay() {
  const { token } = useParams();
  const fetcher = useCallback(() => api.get(`/display/${encodeURIComponent(token)}/command-centre`), [token]);
  const { data, error } = useCommandFeed(fetcher);
  useKioskScreen();

  if (error === 404) return <Message invalid />;
  if (!data) return <Message />;
  return (
    <div className="fixed inset-0 bg-[#050505] cursor-none" data-testid="display-page">
      <ScaleCanvas>
        <CommandCentre data={data} stale={!!error} mode="display" />
      </ScaleCanvas>
    </div>
  );
}
